import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import styles from './NotificationPermissionPopup.module.css';
import { useAlert } from "react-alert";

const NotificationPermissionPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const alert = useAlert();

  useEffect(() => {
    checkNotificationPermission();
  }, []);

  const checkNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return;
    }

    const permission = Notification.permission;
    setPermissionStatus(permission);

    const hasSeenPopup = sessionStorage.getItem('notificationPermissionPopupSeen');
    const isLoggedIn = localStorage.getItem('token');
    
    if (isLoggedIn && permission !== 'granted' && !hasSeenPopup) {
      setTimeout(() => {
        setShowPopup(true);
      }, 2000);
    }
  };

  const handleEnableNotifications = async () => {
    setIsRequesting(true);
    
    const currentPermission = Notification.permission;

    if (currentPermission === 'denied') {
      alert.error('Notifications are blocked. To enable:\n\n1. Click the lock icon (🔒) in your address bar\n2. Click "Site settings"\n3. Change Notifications to "Allow"\n4. Refresh the page');
      sessionStorage.setItem('notificationPermissionPopupSeen', 'true');
      setShowPopup(false);
      setIsRequesting(false);
      return;
    }
    
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);

      if (permission === 'granted') {
        alert.success('Notifications enabled! You will now receive updates about orders, missed opportunities, and more.');
        sessionStorage.setItem('notificationPermissionPopupSeen', 'true');
        setShowPopup(false);
        
        window.location.reload();
      } else if (permission === 'denied') {
        alert.error('Notifications were blocked. To enable: Click the lock icon (🔒) in your browser address bar → Site settings → Allow notifications');
        sessionStorage.setItem('notificationPermissionPopupSeen', 'true');
        setShowPopup(false);
      } else {
        alert.show('Please click "Allow" in the browser prompt to enable notifications.');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      alert.error('An error occurred while enabling notifications.');
    } finally {
      setIsRequesting(false);
    }
  };

  const handleLater = () => {
    sessionStorage.setItem('notificationPermissionPopupSeen', 'true');
    setShowPopup(false);
  };

  // Don't render anything if popup shouldn't be shown
  if (!showPopup) {
    return null;
  }

  return (
    <Modal 
      show={showPopup} 
      onHide={handleLater}
      centered 
      backdrop="static"
      keyboard={false}
      className={styles.modal}
      dialogClassName={styles.dialog}
    >
      <Modal.Header className={styles.header}>
        <Modal.Title className={styles.title}>
          <div className={styles.iconWrapper}>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="40" 
              height="40" 
              fill="#ffffff" 
              className={styles.icon} 
              viewBox="0 0 24 24"
            >
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
            </svg>
          </div>
          <span className='fs-18'>Enable Notifications</span>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className={styles.body}>
        <div className={styles.benefits}>
          <p className={styles.description}>
            Get instant alerts for new orders and important updates
          </p>
          <div className={styles.benefitsList}>
            <div className={styles.benefitItem}>
              <span className={styles.benefitIcon}>🎯</span>
              <span className={styles.benefitText}>Grab orders instantly</span>
            </div>
            <div className={styles.benefitItem}>
              <span className={styles.benefitIcon}>📦</span>
              <span className={styles.benefitText}>Order status updates</span>
            </div>
            <div className={styles.benefitItem}>
              <span className={styles.benefitIcon}>⏰</span>
              <span className={styles.benefitText}>Never miss opportunities</span>
            </div>
          </div>
        </div>
      </Modal.Body>
      
      <Modal.Footer className={styles.footer}>
        <Button 
          variant="secondary" 
          onClick={handleLater}
          disabled={isRequesting}
          className={styles.laterButton}
        >
          Later
        </Button>
        <Button 
          variant="primary" 
          onClick={handleEnableNotifications}
          disabled={isRequesting}
          className={styles.enableButton}
        >
          {isRequesting ? 'Enabling...' : 'Enable'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NotificationPermissionPopup;
